/**
 * SDT (Structured Document Tag) reader for the Word add-in.
 *
 * Reads content controls from the active Word document that match the
 * `advice.reserve.*` tag convention. Parses currency text values into
 * numbers for submission to the gateway.
 *
 * Tag convention (from templates/TAG-MAPPING.md):
 *   advice.reserve.{account-code}           → Individual reserve value
 *   advice.reserve.subtotal.{category-code} → Category subtotal (dgr, dsr, csr, gvr)
 *   advice.reserve.total                    → Grand total
 *
 * @module office/sdt-reader
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A single reserve value read from a content control. */
export interface SdtReserveValue {
  /** The account code (e.g., "general-damages", "past-economic-loss") */
  accountCode: string;
  /** The parsed numeric amount */
  amount: number;
  /** The raw text from the content control */
  rawText: string;
  /** The full tag value (e.g., "advice.reserve.general-damages") */
  tag: string;
}

/** Result of scanning the document for reserve content controls. */
export interface SdtScanResult {
  /** Individual reserve values (excludes subtotals and grand total) */
  reserves: SdtReserveValue[];
  /** Category subtotals (keyed by category code: dgr, dsr, csr, gvr) */
  subtotals: Record<string, SdtReserveValue>;
  /** Grand total value, if found */
  grandTotal: SdtReserveValue | null;
  /** Total number of content controls in the document */
  totalControls: number;
  /** Number of controls with advice.reserve.* tags */
  reserveControls: number;
  /** Tags that were found but could not be parsed */
  parseErrors: Array<{ tag: string; rawText: string; error: string }>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const RESERVE_TAG_PREFIX = 'advice.reserve.';
const SUBTOTAL_PREFIX = 'subtotal.';

// ---------------------------------------------------------------------------
// Currency parsing
// ---------------------------------------------------------------------------

/**
 * Parse a currency string into a number.
 *
 * Handles common formats entered by lawyers:
 *   "$105,000"    → 105000
 *   "$105,000.00" → 105000
 *   "105000"      → 105000
 *   "$0"          → 0
 *   "-$5,000"     → -5000
 *   "($5,000)"    → -5000  (accounting negative)
 *   ""            → 0
 *   "$0" placeholder text → 0
 *
 * @returns The parsed number, or NaN if unparseable.
 */
export function parseCurrency(text: string): number {
  if (!text || text.trim() === '') return 0;

  let cleaned = text.trim();

  // Handle placeholder text (e.g., "Click to enter amount")
  if (!/\d/.test(cleaned)) return 0;

  // Handle accounting-style negatives: ($5,000) → -5000
  const isAccountingNegative = cleaned.startsWith('(') && cleaned.endsWith(')');
  if (isAccountingNegative) {
    cleaned = cleaned.slice(1, -1);
  }

  // Track explicit negative sign
  const isNegative = cleaned.startsWith('-') || isAccountingNegative;
  cleaned = cleaned.replace(/^-/, '');

  // Remove currency symbols and whitespace
  cleaned = cleaned.replace(/[$£€¥\s]/g, '');

  // Remove thousands separators (commas)
  cleaned = cleaned.replace(/,/g, '');

  // Parse the remaining number
  const value = parseFloat(cleaned);
  if (isNaN(value)) return NaN;

  return isNegative ? -value : value;
}

// ---------------------------------------------------------------------------
// Document scanning
// ---------------------------------------------------------------------------

/**
 * Read all reserve values from content controls in the active document.
 *
 * Uses the Office.js Word API to enumerate content controls, filter by
 * the `advice.reserve.*` tag prefix, and parse currency values.
 *
 * @throws If Office.js is not available or the document cannot be accessed.
 */
export async function readReserveValues(): Promise<SdtScanResult> {
  return Word.run(async (context) => {
    const controls = context.document.contentControls;
    controls.load('tag,text');
    await context.sync();

    const totalControls = controls.items.length;
    const reserves: SdtReserveValue[] = [];
    const subtotals: Record<string, SdtReserveValue> = {};
    let grandTotal: SdtReserveValue | null = null;
    const parseErrors: SdtScanResult['parseErrors'] = [];
    let reserveControls = 0;

    for (const cc of controls.items) {
      const tag = (cc.tag || '').trim();
      if (!tag.startsWith(RESERVE_TAG_PREFIX)) continue;

      reserveControls++;
      const suffix = tag.slice(RESERVE_TAG_PREFIX.length);
      const rawText = (cc.text || '').trim();
      const amount = parseCurrency(rawText);

      if (isNaN(amount)) {
        parseErrors.push({
          tag,
          rawText,
          error: `Could not parse "${rawText}" as a currency value`,
        });
        continue;
      }

      const entry: SdtReserveValue = { accountCode: suffix, amount, rawText, tag };

      if (suffix === 'total') {
        // Grand total
        grandTotal = entry;
      } else if (suffix.startsWith(SUBTOTAL_PREFIX)) {
        // Category subtotal (e.g., subtotal.dgr)
        const categoryCode = suffix.slice(SUBTOTAL_PREFIX.length);
        entry.accountCode = categoryCode;
        subtotals[categoryCode] = entry;
      } else {
        // Individual reserve row
        reserves.push(entry);
      }
    }

    return {
      reserves,
      subtotals,
      grandTotal,
      totalControls,
      reserveControls,
      parseErrors,
    };
  });
}

/**
 * Write reserve values back to advice.reserve.* content controls in the document.
 *
 * Finds each content control by its tag and sets its text to the AUD-formatted
 * currency string. Controls not present in the values map are left unchanged.
 *
 * @param values - Map of accountCode → numeric amount (e.g., { 'general-damages': 105000 })
 */
export async function writeReserveValues(values: Record<string, number>): Promise<void> {
  return Word.run(async (context) => {
    const controls = context.document.contentControls;
    controls.load('tag');
    await context.sync();

    for (const cc of controls.items) {
      const tag = (cc.tag || '').trim();
      if (!tag.startsWith(RESERVE_TAG_PREFIX)) continue;

      const suffix = tag.slice(RESERVE_TAG_PREFIX.length);

      if (suffix === 'total') {
        // Grand total — sum all individual values
        const total = Object.values(values).reduce((s, v) => s + v, 0);
        cc.insertText(formatCurrency(total), 'Replace');
      } else if (suffix.startsWith(SUBTOTAL_PREFIX)) {
        // Subtotals are computed — skip (they update when rows update if using formula controls)
        // For plain-text controls, we leave them for the panel to compute separately
      } else if (suffix in values) {
        cc.insertText(formatCurrency(values[suffix]), 'Replace');
      }
    }

    await context.sync();
  });
}

/** Format a number as AUD currency text for content controls. */
function formatCurrency(value: number): string {
  return value.toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

/**
 * Read the full body text of the active document (for AI analysis).
 * Falls back to a sample document in mock/test environments (no Office.js).
 */
export async function readDocumentText(): Promise<string> {
  if (typeof Word === 'undefined') {
    return [
      'RESERVE RECOMMENDATION SCHEDULE',
      '',
      'Reserves    Low    High    Recommended',
      'General Damages    100000    200000    150000',
      'Past Economic Loss    30000    60000    45000',
      'Future Economic Loss    60000    120000    85000',
      'Past Medical    15000    30000    22500',
      'Future Medical    40000    90000    65000',
      'Past Care    8000    18000    12000',
      'Own Legal Costs    25000    45000    35000',
    ].join('\n');
  }
  return Word.run(async (context) => {
    const body = context.document.body;
    body.load('text');
    await context.sync();
    return body.text;
  });
}

/**
 * Quick scan that returns just the control counts (no value parsing).
 * Useful for status checks without the overhead of parsing.
 */
export async function scanControlCounts(): Promise<{
  total: number;
  tagged: number;
  reserveTags: number;
}> {
  try {
    return await Word.run(async (context) => {
      const controls = context.document.contentControls;
      controls.load('tag');
      await context.sync();

      let tagged = 0;
      let reserveTags = 0;

      for (const cc of controls.items) {
        if (cc.tag) {
          tagged++;
          if (cc.tag.startsWith(RESERVE_TAG_PREFIX)) {
            reserveTags++;
          }
        }
      }

      return { total: controls.items.length, tagged, reserveTags };
    });
  } catch {
    return { total: -1, tagged: -1, reserveTags: -1 };
  }
}
